import { Component, inject, Input, OnInit, Optional, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { DataPluginStoreService } from "../../services/store/data-plugin/data-plugin.store.service";
import { PluginConfig } from "../../services/store/data-plugin/external.bookt-transaction.store.collection";
import { PluginConfigService } from "../../services/store/data-plugin/plugin-config.service";
import { generatePluginApiKey } from "../../services/store/data-plugin/plugin-credential-fields";
import { AUSTRALIAN_SUPER_PLUGIN_NAME } from "../../services/store/data-plugin/australian-super.plugin-config";
import {
  messageForResendOtpFailure,
  parsePluginAuthResponse,
  PLUGIN_AUTH_RESPONSE,
  PluginAuthPhase,
  ParsedAuthResult,
} from "../../services/store/data-plugin/australian-super-auth";
import {
  isSuperPluginTerminalErrorMessage,
  messageForSuperPluginSyncFailure,
  SuperPluginTerminalError,
} from "../../services/store/data-plugin/australian-super-teardown";
import { Button } from "@bitwarden/web-vault/app/components/buttons/gloss-button/button";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { UserStoreService } from "../../services/store/user/user.store.service";
import { AppStateStoreService } from "../../services/store/app-state/app-state.store.service";
import { PluginAccountSyncService } from "../../services/syncing/plugin-account-sync.service";
import { ConfirmationDialogService } from "../../services/confirmation/confirmation.service";
import { ConfirmationEnum } from "../../models/enum/confirmation.enum";
import { AccountView } from "../../models/view/account/account.view";
import {
  filterNamedPluginAccounts,
  PluginAccountsMap,
  PluginAccountResponse,
} from "../../shared/utils/helper.plugin/plugin.accounts.map";
import { getPluginMetadata, PluginMetadata } from "../../models/data/plugin-metadata";
import {
  firstAvailableProduct,
  resolvePluginInstitution,
} from "../../shared/utils/helper.institution/plugin-institution.helper";
import { existingPluginAccountNames } from "../../shared/utils/helper.plugin/plugin-account-name.util";
import { LogService } from "@bitwarden/common/abstractions/log.service";

type AuthPayload = { response: string; identifier: string | null };

/** The saved login signed in, but the account whose Fetch data was pressed isn't one of its own. */
class PluginAccountNotInLoginError extends Error {}

/** i18n key for {@link PluginAccountNotInLoginError}. */
const ACCOUNT_NOT_IN_SAVED_LOGIN = "pluginAccountNotInSavedLogin";

@Component({
  selector: "app-plugin-config",
  templateUrl: "./plugin-config.component.html",
})
export class PluginConfigComponent implements OnInit {
  @Input() pluginId: string;
  /** When true (account Sync): use saved creds and go straight to OTP after login. */
  @Input() syncOnly = false;
  /** When true: force re-authentication / credential re-entry instead of auto-using saved creds. */
  @Input() forceReauth = false;
  /** Set by account-details' Fetch data: sync only this account, not every account on the login. */
  @Input() accountId?: string;
  /** When true: use the redesigned Install-flow markup (header, card, inline submit button).
   * Default false keeps the legacy rendering. */
  @Input() redesigned = false;
  /** When true: render only the redesigned fields and submit button (no header/card), for use inside
   * another panel's own card, e.g. the "Sign in again" card of a plugin's Manage panel. */
  @Input() embedded = false;
  /** Overrides the submit button text (default "Activate & Next"). */
  @Input() submitLabel?: string;

  /** The redesigned markup is opt-in; `embedded` implies it. */
  get useRedesign(): boolean {
    return this.redesigned || this.embedded;
  }

  pluginMetadata: PluginMetadata;
  pluginName: string;
  pluginImage: string;

  private dataPluginStoreService = inject(DataPluginStoreService);
  private fb = inject(FormBuilder);
  protected i18nService: I18nService = inject(I18nService);
  private userStore = inject(UserStoreService);
  private appState = inject(AppStateStoreService);
  private pluginAccountSync = inject(PluginAccountSyncService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private pluginConfigService = inject(PluginConfigService);
  private cdr = inject(ChangeDetectorRef);
  private logService = inject(LogService);
  @Optional() private dialogRef: MatDialogRef<PluginConfigComponent> = inject(MatDialogRef, {
    optional: true,
  });

  pluginConfig: PluginConfig;
  configForm: FormGroup;
  otpForm: FormGroup;
  /** Legacy opal-style update: hide form and auto-sync. Super uses authPhase instead. */
  isUpdate: boolean = false;
  /** Australian Super multi-step auth UI. */
  authPhase: PluginAuthPhase = "credentials";
  authErrorMessage: string | null = null;
  authIdentifier: string | null = null;
  /** True after a submit with missing fields, so the form can say what is wrong. */
  showIncompleteHint = false;

  nextButton: Button = new ActionButton({
    text: "Activate & Next",
    type: "submit",
    onClick: this.onSubmit.bind(this),
    isEnabled: true,
  });

  verifyOtpButton: Button = new ActionButton({
    text: "Verify",
    type: "button",
    onClick: this.submitOtp.bind(this),
    isEnabled: true,
  });

  resendOtpButton: Button = new ActionButton({
    text: "Resend code",
    type: "button",
    onClick: this.resendOtp.bind(this),
    isEnabled: true,
  });

  constructor() {}

  ngOnInit(): void {
    this.pluginMetadata = getPluginMetadata(this.pluginId);
    if (this.pluginMetadata) {
      this.pluginImage = this.pluginMetadata.imagePath;
      this.pluginName = this.pluginId;
    }

    this.loadPluginConfig();
    this.createForm();
    this.otpForm = this.fb.group({
      otp: ["", [Validators.required]],
    });

    if (this.forceReauth) {
      this.isUpdate = false;
    }

    if (this.syncOnly && this.isSuperPlugin()) {
      void this.startSyncOnlyFlow();
    } else {
      this.loadSavedCredentialsAndAutoSync();
    }
  }

  /**
   * Account Sync button: saved credentials → beginAuth → OTP (no credential form).
   */
  private async startSyncOnlyFlow(): Promise<void> {
    const formFieldNames = Object.keys(this.configForm.controls);
    const savedCredentials = this.pluginConfigService.getSavedCredentials(
      this.pluginId,
      formFieldNames,
    );

    if (!savedCredentials) {
      this.authPhase = "credentials";
      this.authErrorMessage = this.i18nService.t("australianSuperNoSavedCredentials");
      this.cdr.detectChanges();
      return;
    }

    this.isUpdate = true;
    this.configForm.patchValue(savedCredentials);
    await this.beginAuth();
  }

  isSuperPlugin(): boolean {
    return this.pluginName?.toLowerCase().trim() === AUSTRALIAN_SUPER_PLUGIN_NAME;
  }

  /**
   * Load saved credentials if they exist.
   * Non-Super plugins with saved creds auto-run sync (update flow).
   * Super: restore creds only — user connects via beginAuth → OTP → sync.
   */
  private async loadSavedCredentialsAndAutoSync(): Promise<void> {
    try {
      if (this.forceReauth) {
        return;
      }
      const formFieldNames = Object.keys(this.configForm.controls);
      const savedCredentials = this.pluginConfigService.getSavedCredentials(
        this.pluginId,
        formFieldNames,
      );

      if (!savedCredentials) {
        this.explainMissingSavedCredentials();
        return;
      }

      this.isUpdate = true;
      this.configForm.patchValue(savedCredentials);
      Object.keys(this.configForm.controls).forEach((key) => {
        const control = this.configForm.controls[key];
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.configForm.updateValueAndValidity();

      const hasAllRequiredFields = formFieldNames.every((fieldName) => {
        const control = this.configForm.get(fieldName);
        const value = control?.value;
        return control && value && value.toString().trim() !== "";
      });

      if (!hasAllRequiredFields) {
        this.isUpdate = false;
        this.explainMissingSavedCredentials();
        return;
      }

      if (this.isSuperPlugin()) {
        this.authPhase = "credentials";
        this.cdr.detectChanges();
        return;
      }

      this.cdr.detectChanges();
      Promise.resolve().then(async () => {
        try {
          const syncedAnyAccount = await this.runLegacyPluginSync();
          if (syncedAnyAccount) {
            await this.confirmationDialog.confirmFor(ConfirmationEnum.pluginSyncCompleted);
          }
          if (this.dialogRef) {
            this.dialogRef.close();
          }
        } catch (error) {
          this.logService.error("Auto-sync failed: " + JSON.stringify(error));
          this.confirmationDialog.notify(
            "error",
            "error",
            error instanceof PluginAccountNotInLoginError
              ? ACCOUNT_NOT_IN_SAVED_LOGIN
              : "Auto-sync failed. Please check your credentials.",
          );
          // Show the form again (the template swaps it for a spinner while isUpdate) so the
          // stored credentials can be corrected right here. The resubmit is then a fresh
          // entry: new apiKey, saved once auth succeeds (#5510).
          this.isUpdate = false;
          this.cdr.detectChanges();
        }
      });
    } catch (error) {
      this.logService.error("Error loading saved credentials: " + JSON.stringify(error));
      this.isUpdate = false;
    }
  }

  /**
   * Fetch data (syncOnly) expects saved credentials. Without them the dialog falls back to the
   * credential form, so say why instead of showing the form unexplained (#5450).
   */
  private explainMissingSavedCredentials(): void {
    if (this.syncOnly) {
      this.authErrorMessage = this.i18nService.t("pluginNoSavedCredentialsForFetch");
    }
  }

  private loadPluginConfig(): void {
    const configs = this.dataPluginStoreService.booktTransaction.configs;
    const normalize = (s: string) => s?.toLowerCase().trim() ?? "";
    this.pluginConfig = configs.find((p) => normalize(p.name) === normalize(this.pluginId));
  }

  private findPluginConfig(): PluginConfig {
    const normalize = (s: string) => s?.toLowerCase().trim() ?? "";
    const plugin = this.dataPluginStoreService.booktTransaction.configs.find(
      (p) => normalize(p.name) === normalize(this.pluginName),
    );
    if (!plugin) {
      throw new Error("Plugin not found");
    }
    return plugin;
  }

  private createForm(): void {
    if (!this.pluginConfig || !this.pluginConfig.fields) {
      this.configForm = this.fb.group({});
      return;
    }

    const formControls: { [key: string]: any } = {};
    this.pluginConfig.fields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      formControls[field.field] = [field.defaultValue || "", validators];
    });

    this.configForm = this.fb.group(formControls);
  }

  /**
   * Browser autofill can fill the visible inputs without Angular seeing it (Chrome hides autofilled
   * values from the page until the first interaction), leaving the form "empty" and the submit a
   * silent no-op. Read the fields' current DOM values so what the user sees is what gets submitted.
   */
  private syncAutofilledValues(formEl: HTMLFormElement): void {
    const inputs = Array.from(
      formEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea"),
    );
    (this.pluginConfig?.fields ?? []).forEach((field, i) => {
      const control = this.configForm.get(field.field);
      const domValue = inputs[i]?.value;
      if (control && domValue && control.value !== domValue) {
        control.setValue(domValue);
      }
    });
  }

  async onSubmit(event?: Event): Promise<void> {
    this.nextButton.enableButton(false);
    this.authErrorMessage = null;
    this.showIncompleteHint = false;

    const formEl = event?.target;
    if (formEl instanceof HTMLFormElement) {
      this.syncAutofilledValues(formEl);
    }

    if (!this.configForm.valid) {
      this.configForm.markAllAsTouched();
      this.showIncompleteHint = true;
      this.nextButton.enableButton(true);
      return;
    }

    try {
      if (this.isSuperPlugin()) {
        await this.beginAuth();
        return;
      }

      if (this.dialogRef) {
        this.dialogRef.close();
      }
      const syncedAnyAccount = await this.runLegacyPluginSync();
      if (syncedAnyAccount) {
        await this.confirmationDialog.confirmFor(ConfirmationEnum.pluginSyncCompleted);
      }
    } catch (error) {
      this.confirmationDialog.notify(
        "error",
        "error",
        error instanceof PluginAccountNotInLoginError
          ? ACCOUNT_NOT_IN_SAVED_LOGIN
          : "somethingWentWrong",
      );
    } finally {
      // An embedded form (Manage panel) isn't closed on success, so it must be usable again (#6216).
      this.nextButton.enableButton(true);
    }
  }

  /** Step 1 (Super): id + pin → need_otp or authenticated. */
  async beginAuth(): Promise<void> {
    this.authPhase = "authenticating";
    this.authErrorMessage = null;
    this.cdr.detectChanges();

    try {
      const auth = await this.callAuth(this.configForm.value);
      // Super only (onSubmit and startSyncOnlyFlow call this just for Super): credentials are
      // saved after OTP is verified, in continueSync, so a cancelled login never persists them.
      await this.handleAuthResponse(auth);
    } catch (error) {
      this.logService.error("beginAuth failed: " + JSON.stringify(error));
      if (this.isSuperPlugin() && this.isTerminalFailureError(error)) {
        await this.handleSuperTerminalFailure(messageForSuperPluginSyncFailure(error));
        return;
      }
      this.authPhase = "credentials";
      this.authErrorMessage = this.i18nService.t("connectionFailedPleaseTryAgain");
      this.nextButton.enableButton(true);
      this.cdr.detectChanges();
    }
  }

  /** Step 2 (Super): submit OTP code. */
  async submitOtp(): Promise<void> {
    if (!this.otpForm.valid || !this.authIdentifier) {
      return;
    }

    this.authErrorMessage = null;
    this.verifyOtpButton.enableButton(false);
    this.authPhase = "authenticating";
    this.cdr.detectChanges();

    try {
      const auth = await this.callAuth({
        otp: this.otpForm.value.otp,
        identifier: this.authIdentifier,
      });
      await this.handleAuthResponse(auth);
    } catch (error) {
      this.logService.error("submitOtp failed: " + JSON.stringify(error));
      if (this.isTerminalFailureError(error)) {
        await this.handleSuperTerminalFailure(messageForSuperPluginSyncFailure(error));
        return;
      }
      this.authPhase = "otp";
      this.authErrorMessage = this.i18nService.t("verificationFailedPleaseTryAgain");
    } finally {
      this.verifyOtpButton.enableButton(true);
      this.cdr.detectChanges();
    }
  }

  /** Resend OTP while session is open. */
  async resendOtp(): Promise<void> {
    if (!this.authIdentifier) {
      return;
    }

    this.resendOtpButton.enableButton(false);
    this.authErrorMessage = null;

    try {
      const auth = await this.callAuth({
        send: true,
        identifier: this.authIdentifier,
      });
      if (auth.response === PLUGIN_AUTH_RESPONSE.OTP_SENT) {
        this.authPhase = "otp";
        this.authErrorMessage = null;
        return;
      }
      await this.handleSuperTerminalFailure(messageForResendOtpFailure(auth.response));
    } catch (error) {
      this.logService.error("resendOtp failed: " + JSON.stringify(error));
      // Network/unexpected error: keep user on the OTP screen with an inline message rather than
      // closing the dialog. handleSuperTerminalFailure is reserved for server-side terminal states.
      this.authErrorMessage = this.i18nService.t("couldNotResendCodePleaseTryAgain");
    } finally {
      this.resendOtpButton.enableButton(true);
      this.cdr.detectChanges();
    }
  }

  private async handleAuthResponse(auth: AuthPayload): Promise<void> {
    const parsed = parsePluginAuthResponse(auth.response, auth.identifier, this.authIdentifier);
    await this.applyParsedAuthResult(parsed);
  }

  private async applyParsedAuthResult(parsed: ParsedAuthResult): Promise<void> {
    switch (parsed.kind) {
      case "need_otp":
        this.authIdentifier = parsed.identifier;
        this.authPhase = "otp";
        this.otpForm.reset({ otp: "" });
        this.nextButton.enableButton(true);
        break;

      case "success":
        this.authIdentifier = parsed.identifier;
        await this.continueSync(parsed.identifier);
        break;

      case "credential_error":
        this.authPhase = "credentials";
        this.authErrorMessage = parsed.message;
        this.nextButton.enableButton(true);
        break;

      case "otp_retry":
        this.authIdentifier = parsed.identifier;
        this.authPhase = "otp";
        this.authErrorMessage = parsed.message;
        this.verifyOtpButton.enableButton(true);
        break;

      case "pending":
        this.authIdentifier = parsed.identifier;
        this.authPhase = "otp";
        break;

      case "terminal":
        await this.handleSuperTerminalFailure(parsed.message);
        break;
    }
    this.cdr.detectChanges();
  }

  private isTerminalFailureError(error: unknown): boolean {
    if (error instanceof SuperPluginTerminalError) {
      return true;
    }
    const message = error instanceof Error ? error.message : String(error ?? "");
    return isSuperPluginTerminalErrorMessage(message);
  }

  private endSuperImportingProcess(): void {
    this.appState.endProcess("importing-data");
  }

  /**
   * Terminal auth/sync failure (S5 server teardown): stop spinners, notify, close plugin modal.
   */
  private async handleSuperTerminalFailure(message: string): Promise<void> {
    this.endSuperImportingProcess();
    this.authIdentifier = null;
    this.authPhase = "credentials";
    this.authErrorMessage = null;
    this.otpForm.reset({ otp: "" });
    this.nextButton.enableButton(true);
    this.verifyOtpButton.enableButton(true);
    this.cdr.detectChanges();
    this.confirmationDialog.notify("error", "Australian Super", message, false);
    this.dialogRef?.close();
  }

  /** Step 3 (Super): accounts + transactions after auth success. */
  async continueSync(identifier: string): Promise<void> {
    this.authPhase = "syncing";
    this.authErrorMessage = null;
    this.cdr.detectChanges();

    try {
      // Save Super credentials only after OTP is verified (not on syncOnly — creds already saved).
      if (this.isSuperPlugin() && !this.syncOnly) {
        await this.savePluginConfig();
      }
      const syncedAnyAccount = await this.syncAccountsAndTransactions(identifier);
      if (syncedAnyAccount) {
        await this.confirmationDialog.confirmFor(ConfirmationEnum.pluginSyncCompleted);
      }
      if (this.dialogRef) {
        this.dialogRef.close();
      } else {
        // Embedded in the Manage panel (#6216): nothing closes it, so go back to the form.
        this.authPhase = "credentials";
      }
    } catch (error) {
      this.logService.error("continueSync failed: " + JSON.stringify(error));
      await this.handleSuperTerminalFailure(messageForSuperPluginSyncFailure(error));
    }
  }

  private async callAuth(inputs: Record<string, unknown>, apiKey?: string): Promise<AuthPayload> {
    const plugin = this.findPluginConfig();

    const result = await this.dataPluginStoreService.booktTransaction.fetch({
      type: "auth",
      config: plugin.id,
      inputs,
      ...(apiKey ? { apiKey } : {}),
    });

    return {
      response: result?.response ?? "",
      identifier: result?.identifier ?? null,
    };
  }

  /** Returns whether any account was actually synced, so callers can skip the "Sync completed!"
   * confirmation when nothing came through. */
  private async syncAccountsAndTransactions(identifier: string): Promise<boolean> {
    // Read before listAccounts() saves the fetched accounts, so "N added" below counts only the
    // ones that are new to the vault.
    const knownAccountIds = new Set(
      (this.userStore.accounts.accountViews() ?? []).map((account) => account.id),
    );
    const processMessageAccounts = "Fetching accounts...";
    this.appState.startProcess("importing-data", processMessageAccounts);
    const accounts = await this.listAccounts(identifier);
    this.appState.endProcess("importing-data");

    if (!accounts?.length) {
      if (this.accountId) {
        // Fetch data for an account this login doesn't have, e.g. the second of two Opal logins
        // when only the last one saved is stored. Say so, not "sync completed" (#5450).
        throw this.isSuperPlugin()
          ? new SuperPluginTerminalError(this.i18nService.t(ACCOUNT_NOT_IN_SAVED_LOGIN))
          : new PluginAccountNotInLoginError();
      }
      return false;
    }

    // Give feedback as soon as new accounts are connected, rather than leaving the user without
    // any confirmation until the (potentially slow) transaction fetch below finishes and the
    // "Sync completed" dialog appears. Counted from the vault rather than from isUpdate/
    // forceReauth, which several resyncs of an already-linked plugin reach with both false
    // (e.g. the corrected resubmit after a failed auto-sync); a pure resync shows no toast.
    const addedCount = accounts.filter((account) => !knownAccountIds.has(account.id)).length;
    if (addedCount > 0) {
      this.pluginAccountSync.notifyAccountsAdded(addedCount, this.pluginName);
    }

    const plugin = this.findPluginConfig();
    const synced = await this.pluginAccountSync.syncAccounts(accounts, {
      configId: plugin.id,
      identifier,
      filterFromStartDate: this.isSuperPlugin(),
      // Australian Super's server teardown ends the whole batch, not just this account.
      abortBatchOn: (error) =>
        this.isSuperPlugin() && this.isTerminalFailureError(error)
          ? new SuperPluginTerminalError(messageForSuperPluginSyncFailure(error))
          : undefined,
    });
    return synced.length > 0;
  }

  /**
   * Non-Super plugins: authenticate, then save the credentials only if auth succeeded and they
   * are new or differ from the stored ones (#5510). Saving first kept a rejected password in
   * the vault, and a failed auto-sync left no way to enter a corrected one.
   */
  private async runLegacyPluginSync(): Promise<boolean> {
    const formValue = this.configForm.value as Record<string, string>;
    const formFieldNames = Object.keys(this.configForm.controls);
    // Also ask for "apiKey" so the stored key comes back even if the form has no apiKey field.
    const stored = this.pluginConfigService.getSavedCredentials(this.pluginId, [
      ...formFieldNames,
      "apiKey",
    ]);
    // Pass the apiKey that will be saved, so every path keeps the Authorization header it sent
    // before #5510. The auto-sync with the stored row (`isUpdate`) used whatever key that row
    // has, even none. Every other submit (first install, "Switch user" via forceReauth, a stored
    // row missing required fields, and the retry after a failed auto-sync, which resets
    // isUpdate) saved first with a freshly generated key, so don't reuse the stored key there.
    const apiKey =
      formValue.apiKey ||
      (this.isUpdate ? stored?.apiKey : generatePluginApiKey(this.pluginConfig.name));

    const processMessageAuth = "Authenticating...";
    this.appState.startProcess("importing-data", processMessageAuth);
    const auth = await this.callAuth(formValue, apiKey);
    this.appState.endProcess("importing-data");

    if (!auth.identifier) {
      throw new Error(auth.response || "Authentication failed");
    }

    const credentialsChanged = formFieldNames.some(
      (field) => String(formValue[field] ?? "") !== String(stored?.[field] ?? ""),
    );
    if (!stored || credentialsChanged || apiKey !== stored.apiKey) {
      await this.savePluginConfig(apiKey);
    }

    return this.syncAccountsAndTransactions(auth.identifier);
  }

  private async listAccounts(username: string): Promise<AccountView[]> {
    const plugin = this.findPluginConfig();

    let accountResponse: PluginAccountResponse;
    try {
      accountResponse = (await this.dataPluginStoreService.booktTransaction.fetch({
        type: "accounts",
        config: plugin.id,
        inputs: { id: username },
      })) as PluginAccountResponse;
    } catch (error) {
      if (this.isSuperPlugin() && this.isTerminalFailureError(error)) {
        throw new SuperPluginTerminalError(messageForSuperPluginSyncFailure(error));
      }
      throw error;
    }

    if (this.isSuperPlugin() && !accountResponse?.account?.length) {
      throw new SuperPluginTerminalError(this.i18nService.t("couldNotLoadAustralianSuperAccount"));
    }

    const namedAccounts = filterNamedPluginAccounts(accountResponse.account, this.pluginName);

    if (namedAccounts.length === 0) {
      // Silent on an auto-sync/re-auth of a linked plugin, as before; only the notice is gated
      // here, there's nothing to sync either way.
      if (this.isUpdate || this.forceReauth) {
        return [];
      }
      this.confirmationDialog.notify("error", "error", "pluginNoNamedAccountsFound");
      return [];
    }

    // No "already added" block here: this component only sees accounts already in the vault when
    // it's resyncing/reconnecting an already-linked plugin, and that's reached with isUpdate false
    // too (the corrected resubmit after a failed auto-sync, account-details "Fetch data" without
    // saved credentials, a saved row missing a field). Re-syncing them can't duplicate anything:
    // saveToVault upserts by id, and PluginAccountSyncService.syncAccounts dedupes transactions
    // against the vault.
    const accountViews = this.userStore.accounts.accountViews() ?? [];

    const institution = await this.getInstitution();
    const product = firstAvailableProduct(institution);
    // On a resync namedAccounts contains already-saved accounts: savedNames lets each keep its
    // name (no renumbering), while a newly-found same-named card continues the count.
    const existingAccountNames = existingPluginAccountNames(accountViews, this.pluginName);
    const savedNames = new Map(accountViews.map((account) => [account.id, account.originalName]));

    const accounts = PluginAccountsMap.toAccountView(
      { account: namedAccounts },
      institution,
      product,
      this.pluginName,
      existingAccountNames,
      savedNames,
    );

    // Placeholder names were already dropped by filterNamedPluginAccounts (isNamedPluginAccount).
    const validAccounts = accounts.filter(
      (acc) =>
        acc.id &&
        // Fetch data on one account touches only that account, not the rest of the login (#5450).
        (!this.accountId || acc.id === this.accountId),
    );

    await this.userStore.accounts.saveToVault(validAccounts, true);

    return validAccounts;
  }

  private async getInstitution() {
    return resolvePluginInstitution(
      this.pluginId,
      this.pluginMetadata?.institutionName,
      this.userStore.institutions.institutionViews() ?? [],
      this.dataPluginStoreService.theInstitutionList.theInstitutionViews() ?? [],
      (institution) => this.userStore.institutions.saveToVault([institution], true),
    );
  }

  /** `apiKey`: save this key rather than let savePluginCredentials generate a new one. */
  private async savePluginConfig(apiKey?: string): Promise<void> {
    if (!this.configForm.valid || !this.pluginConfig) {
      throw new Error("Invalid plugin configuration");
    }

    const formValue = this.configForm.value as Record<string, string>;
    const saved = await this.pluginConfigService.savePluginCredentials(
      this.pluginConfig.name,
      this.pluginConfig.url,
      apiKey ? { ...formValue, apiKey } : formValue,
    );

    if (!saved) {
      throw new Error("Failed to save plugin configuration");
    }
  }

  getFieldType(type: string): string {
    if (type === "password") {
      return "password";
    }
    return "text";
  }
}
